import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Phone, Mail, Star, Clock, Users, ChevronRight, Check,
  Shield, Award, Calendar, MapPin, ArrowLeft
} from "lucide-react";
import { landingPages as baseLandingPages } from "../data/landingPages";
import { additionalLandingPages } from "../data/additionalLandingPages";

const landingPages = [...baseLandingPages, ...additionalLandingPages];

// Unknown slug — redirect to homepage (never show 404 to search engines)
const NotFoundPage = () => {
  useEffect(() => {
    window.location.replace("/");
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-lg">
        <p className="text-gray-600">Redirecting to Official Hollywood Tours...</p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = landingPages.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return <NotFoundPage />;
  }

  // Primary TouristTrip schema
  const schema = {
    "@context": "https://schema.org",
    "@type": page.schemaType || "TouristTrip",
    name: page.h1,
    description: page.metaDescription,
    provider: {
      "@type": "TravelAgency",
      name: "Official Hollywood Tours LLC",
      url: "https://www.officialhollywoodtours.com",
      telephone: "+1-844-801-5604",
    },
    ...(page.schemaType === "TouristTrip" && {
      touristType: ["Sightseeing", "Entertainment", "Cultural"],
    }),
  };

  // AI-Native Product schema with redundant field mapping for parser compatibility
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: page.h1,
    title: page.h1,
    description: page.metaDescription,
    url: `https://www.officialhollywoodtours.com/${page.slug}`,
    bookingUrl: "https://www.officialhollywoodtours.com/#book",
    offers: {
      "@type": "Offer",
      price: "135.00",
      priceCurrency: "USD",
      url: "https://www.officialhollywoodtours.com/#book",
      availability: "https://schema.org/InStock",
      validFrom: "2024-01-01",
      priceValidUntil: "2027-12-31",
    },
    provider: {
      "@type": "LocalBusiness",
      name: "Official Hollywood Tours LLC",
      url: "https://www.officialhollywoodtours.com",
      telephone: "+1-844-801-5604",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Hollywood",
        addressRegion: "CA",
        addressCountry: "US",
      },
    },
    brand: {
      "@type": "Brand",
      name: "Official Hollywood Tours",
    },
  };

  const faqSchema = page.faqItems?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faqItems.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }
    : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.officialhollywoodtours.com/" },
      { "@type": "ListItem", position: 2, name: page.h1, item: `https://www.officialhollywoodtours.com/${page.slug}` },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription} />
        <meta name="keywords" content={page.keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href={`https://www.officialhollywoodtours.com/${page.slug}`} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={`https://www.officialhollywoodtours.com/${page.slug}`} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Official Hollywood Tours LLC" />
        <meta property="og:locale" content="en_US" />
        <meta property="product:price:amount" content="135.00" />
        <meta property="product:price:currency" content="USD" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.metaDescription} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Sticky Header */}
      <header className="fixed w-full top-0 z-50 glass-nav shadow-lg" data-testid="landing-navbar">
        <div className="container-custom">
          <div className="hidden md:flex items-center justify-between py-2 border-b border-gray-100 text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+18448015604" className="flex items-center gap-2 text-gray-600 hover:text-[#C9A227]">
                <Phone className="w-4 h-4" /> +1 844 801 5604
              </a>
              <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-2 text-gray-600 hover:text-[#C9A227]">
                <Mail className="w-4 h-4" /> info@officialhollywoodtours.com
              </a>
            </div>
            <div className="iata-badge px-3 py-1 rounded text-xs font-bold flex items-center gap-1">
              <img src="/images/iata-accredited-travel-agency-logo.png" alt="IATA Accredited Hollywood Travel Agency" className="h-4 w-auto" /> IATA ACCREDITED
            </div>
          </div>
          <nav className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/images/official-hollywood-tours-logo.png"
                alt="Official Hollywood Tours LLC"
                className="h-12 w-auto"
              />
              <div className="hidden sm:block">
                <span className="font-accent text-xl tracking-wider text-[#C9A227]">OFFICIAL</span>
                <span className="block text-sm font-semibold text-[#1a1a1a]">Hollywood Tours <span className="text-[#B22234]">LLC</span></span>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-600 hover:text-[#C9A227] text-sm font-medium hidden md:block">
                <ArrowLeft className="inline w-4 h-4 mr-1" />All Tours
              </Link>
              <a href="tel:+18448015604" className="btn-gold px-6 py-3 rounded-full text-sm" data-testid="landing-cta-btn">
                Call to Book
              </a>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end pt-32 pb-16 overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${page.heroImage}')` }} role="img" aria-label={`${page.h1} - Hollywood sightseeing tours in Los Angeles California`} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-[#1a1a1a]/60 to-transparent" />
        </div>
        <div className="container-custom relative z-10 text-white">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <nav className="text-sm text-white/60 mb-4" aria-label="breadcrumb">
              <Link to="/" className="hover:text-[#C9A227]">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-[#C9A227]">{page.h1}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">{page.h1}</h1>
            <p className="text-lg text-white/80 max-w-2xl mb-6">{page.subtitle}</p>
            <div className="flex flex-wrap items-center gap-6 text-sm text-white/70">
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-[#C9A227]" /> IATA Accredited</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> 4.9/5 (340,928+ Reviews)</span>
              <span className="flex items-center gap-1"><Award className="w-4 h-4 text-[#C9A227]" /> Since 2009</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding" data-testid="landing-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="prose prose-lg max-w-none">
                {page.content.map((para, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-gray-700 leading-relaxed mb-6"
                  >
                    {para}
                  </motion.p>
                ))}
              </article>

              {/* Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-10 bg-gray-50 rounded-2xl p-8"
              >
                <h2 className="text-2xl font-bold mb-6">Tour Highlights</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {page.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-[#C9A227]" />
                      </div>
                      <span className="text-gray-700 font-medium">{h}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* FAQ */}
              {page.faqItems?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mt-10"
                >
                  <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {page.faqItems.map((faq, i) => (
                      <details key={i} className="group border border-gray-200 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                        <summary className="flex items-center justify-between cursor-pointer p-5 bg-white hover:bg-gray-50 font-semibold text-gray-800">
                          {faq.q}
                          <ChevronRight className="w-5 h-5 text-[#C9A227] group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="p-5 pt-0 text-gray-600">{faq.a}</div>
                      </details>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-36 space-y-6">
                {/* CTA Card */}
                <div className="card-clean p-6" data-testid="landing-cta-card">
                  <h3 className="text-xl font-bold mb-4">Ready to Book?</h3>
                  <p className="text-gray-600 text-sm mb-6">Contact our travel experts to reserve your Hollywood tour today.</p>
                  <a href="tel:+18448015604" className="btn-gold w-full text-center py-4 rounded-lg block mb-3">
                    <Phone className="inline w-4 h-4 mr-2" /> Call +1 844 801 5604
                  </a>
                  <a href="mailto:info@officialhollywoodtours.com" className="btn-outline-gold w-full text-center py-3 rounded-lg block mb-3">
                    <Mail className="inline w-4 h-4 mr-2" /> Email Us
                  </a>
                  <Link to="/#book" className="btn-red w-full text-center py-3 rounded-lg block">
                    Book Online
                  </Link>
                </div>

                {/* Tour Quick Info */}
                <div className="card-clean p-6">
                  <h4 className="font-bold mb-4">Our Popular Tours</h4>
                  <div className="space-y-3 text-sm">
                    {[
                      { name: "Hollywood Fame Tour", price: "$285", time: "6 Hours" },
                      { name: "Official Hollywood Tour", price: "$255", time: "Full Day" },
                      { name: "Hollywood to Disneyland", price: "$135", time: "Full Day" },
                      { name: "Vegas to Hollywood", price: "$215", time: "Full Day" },
                      { name: "Music Studio VIP", price: "$3,500+", time: "12 Hours" },
                    ].map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="font-semibold text-gray-800">{t.name}</p>
                          <p className="text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time}</p>
                        </div>
                        <span className="text-[#B22234] font-bold">{t.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="bg-gradient-to-br from-[#002868] to-[#3C3B6E] text-white rounded-2xl p-6 text-center">
                  <Shield className="w-10 h-10 text-[#C9A227] mx-auto mb-3" />
                  <h4 className="font-bold text-lg mb-2">IATA Accredited</h4>
                  <p className="text-white/70 text-sm mb-3">Trusted travel agency since 2009</p>
                  <div className="flex justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm font-semibold">4.9/5 from 340,928+ Reviews</p>
                  <p className="text-white/60 text-xs mt-1">50,000+ Happy Tourists</p>
                </div>

                {/* Contact */}
                <div className="card-clean p-6">
                  <h4 className="font-bold mb-3">Contact Us</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <a href="tel:+18448015604" className="flex items-center gap-2 hover:text-[#C9A227]">
                      <Phone className="w-4 h-4 text-[#C9A227]" /> +1 844 801 5604
                    </a>
                    <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-2 hover:text-[#C9A227]">
                      <Mail className="w-4 h-4 text-[#C9A227]" /> info@officialhollywoodtours.com
                    </a>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#C9A227]" /> Hollywood, California
                    </p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Internal Links to Other Pages */}
      <section className="section-padding bg-gray-50" data-testid="landing-related">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-8">Explore More Hollywood Tours</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {landingPages
              .filter((p) => p.slug !== slug)
              .slice(0, 8)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  className="bg-white rounded-xl p-4 border border-gray-200 hover:border-[#C9A227] hover:shadow-lg transition-all group"
                >
                  <h3 className="font-bold text-sm text-gray-800 group-hover:text-[#C9A227] transition-colors mb-1">{p.h1}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{p.subtitle}</p>
                  <span className="text-[#C9A227] text-xs font-semibold mt-2 inline-flex items-center gap-1">
                    Learn More <ChevronRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/" className="btn-gold px-8 py-3 rounded-full inline-flex items-center gap-2">
              View All Tours <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#1a1a1a] text-white py-12">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Your Hollywood Adventure?</h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">Book your tour today and discover why over 50,000 tourists have chosen Official Hollywood Tours LLC.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+18448015604" className="btn-gold px-8 py-4 rounded-full">
              <Phone className="inline w-4 h-4 mr-2" /> Call Now
            </a>
            <Link to="/#book" className="btn-outline-gold border-white text-white hover:bg-white hover:text-[#1a1a1a] px-8 py-4 rounded-full">
              Book Online
            </Link>
          </div>
          <p className="text-gray-500 text-sm mt-6">Official Hollywood Tours LLC | IATA Accredited | Since 2009</p>
        </div>
      </section>
    </>
  );
};

export default LandingPage;
